// src/admin/pages/ComplianceLogs.tsx — Regulatory Compliance Log Viewer
import { useState, useEffect, useCallback } from 'react';
import useAdmin from '../hooks/useAdmin';
import { Loader2, Download, Shield, AlertTriangle, Info, Filter } from 'lucide-react';
import { toast } from 'react-hot-toast';

interface LogEntry {
  id: string;
  log_type: string;
  table_id: string | null;
  user_id: string | null;
  actor_role: string | null;
  payload: any;
  hash: string;
  severity: 'INFO' | 'WARN' | 'CRITICAL';
  reported_at: string;
}
interface CriticalSummaryItem { log_type: string; severity: string; count: string; }

const SEV_STYLE: Record<string, string> = {
  INFO:     'bg-blue-100 text-blue-700',
  WARN:     'bg-yellow-100 text-yellow-700',
  CRITICAL: 'bg-red-100 text-red-700',
};
const SEV_ICON: Record<string, React.ReactNode> = {
  INFO:     <Info size={12} className="inline mr-1" />,
  WARN:     <AlertTriangle size={12} className="inline mr-1" />,
  CRITICAL: <Shield size={12} className="inline mr-1" />,
};

const LOG_TYPE_GROUPS: Record<string, string[]> = {
  'RTP Events':    ['RTP_CHANGE', 'RTP_AUTO_SCHEDULE', 'RTP_BLOCK', 'VOLATILITY_CHANGE', 'SCHEDULE_UPDATED'],
  'Game Events':   ['SESSION_START', 'SESSION_END', 'JACKPOT_WIN', 'COLD_PERIOD', 'STREAK_SUPPRESSION'],
  'Financial':     ['CREDIT_ADDED', 'CREDIT_DEDUCTED', 'CASHOUT_REQUEST', 'CASHOUT_APPROVED', 'CASHOUT_REJECTED'],
  'Fraud / Risk':  ['FRAUD_FLAG', 'RISK_PLAYER_FLAGGED', 'IP_MISMATCH', 'BOT_DETECTION'],
  'Admin Actions': ['ADMIN_LOGIN', 'MANAGER_LOGIN', 'TABLE_CREATED', 'TABLE_DELETED', 'AUDIT_EXPORT'],
};

export default function ComplianceLogs() {
  const { token, error, setError } = useAdmin();
  const [logs, setLogs]       = useState<LogEntry[]>([]);
  const [total, setTotal]     = useState(0);
  const [summary, setSummary] = useState<CriticalSummaryItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [offset, setOffset]   = useState(0);
  const [expanded, setExpanded] = useState<string | null>(null);
  const LIMIT = 50;

  const [filters, setFilters] = useState({
    log_type: '', severity: '', date_from: '', date_to: ''
  });

  const load = useCallback(async (off = 0) => {
    if (!token) return;
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: String(LIMIT), offset: String(off) });
      if (filters.log_type)  params.set('log_type', filters.log_type);
      if (filters.severity)  params.set('severity', filters.severity);
      if (filters.date_from) params.set('date_from', filters.date_from);
      if (filters.date_to)   params.set('date_to', filters.date_to);

      const [logsRes, sumRes] = await Promise.all([
        fetch(`/admin/compliance-logs?${params}`, { headers: { Authorization: `Bearer ${token}` }, credentials: 'include' }),
        fetch('/admin/compliance-logs/critical-summary', { headers: { Authorization: `Bearer ${token}` }, credentials: 'include' }),
      ]);
      const logsData = await logsRes.json();
      setLogs(logsData.logs || []);
      setTotal(logsData.total || 0);
      setSummary((await sumRes.json()).summary || []);
      setOffset(off);
    } catch (err: any) { setError(err.message); }
    finally { setLoading(false); }
  }, [token, filters, setError]);

  useEffect(() => { load(0); }, [load]);

  const exportReport = async () => {
    try {
      const params = new URLSearchParams();
      if (filters.date_from) params.set('date_from', filters.date_from);
      if (filters.date_to)   params.set('date_to', filters.date_to);
      const res = await fetch(`/admin/compliance-logs/export?${params}`, {
        headers: { Authorization: `Bearer ${token}` }, credentials: 'include'
      });
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `compliance-${Date.now()}.json`;
      a.click(); URL.revokeObjectURL(url);
      toast.success('Export downloaded!');
    } catch (err: any) { toast.error(err.message); }
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex justify-between items-start">
        <div>
          <h2 className="text-3xl font-bold text-gray-900">Compliance Logs</h2>
          <p className="text-gray-500 mt-1">TGCOA-format regulatory audit trail — tamper-proof, SHA-256 hashed</p>
        </div>
        <button onClick={exportReport}
          className="flex items-center gap-2 px-5 py-2.5 bg-green-600 text-white rounded-xl hover:bg-green-700 font-medium shadow">
          <Download size={18} /> Export Report
        </button>
      </div>

      {error && (
        <div className="p-4 bg-red-50 border-l-4 border-red-500 text-red-700 rounded flex justify-between">
          <span>{error}</span><button onClick={() => setError('')} className="font-bold">✕</button>
        </div>
      )}

      {/* Critical Summary */}
      {summary.length > 0 && (
        <div className="bg-orange-50 border border-orange-200 rounded-xl p-4">
          <h3 className="font-semibold text-orange-800 mb-3 flex items-center gap-2">
            <AlertTriangle size={18} /> Last 24h Critical & Warning Events
          </h3>
          <div className="flex flex-wrap gap-3">
            {summary.map(s => (
              <div key={s.log_type} className={`px-3 py-2 rounded-lg text-sm font-medium ${SEV_STYLE[s.severity]}`}>
                {s.log_type}: <strong>{s.count}</strong>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-xl shadow p-4 border border-gray-200">
        <div className="flex flex-wrap gap-4 items-end">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Log Type</label>
            <select value={filters.log_type} onChange={e => setFilters(p => ({ ...p, log_type: e.target.value }))}
              className="border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 min-w-48">
              <option value="">All Types</option>
              {Object.entries(LOG_TYPE_GROUPS).map(([group, types]) => (
                <optgroup key={group} label={group}>
                  {types.map(t => <option key={t} value={t}>{t}</option>)}
                </optgroup>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Severity</label>
            <select value={filters.severity} onChange={e => setFilters(p => ({ ...p, severity: e.target.value }))}
              className="border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500">
              <option value="">All</option>
              <option>INFO</option><option>WARN</option><option>CRITICAL</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">From Date</label>
            <input type="datetime-local" value={filters.date_from}
              onChange={e => setFilters(p => ({ ...p, date_from: e.target.value }))}
              className="border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">To Date</label>
            <input type="datetime-local" value={filters.date_to}
              onChange={e => setFilters(p => ({ ...p, date_to: e.target.value }))}
              className="border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500" />
          </div>
          <button onClick={() => load(0)}
            className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 text-sm font-medium">
            <Filter size={16} /> Apply
          </button>
          <button onClick={() => { setFilters({ log_type: '', severity: '', date_from: '', date_to: '' }); }}
            className="px-4 py-2 bg-gray-100 text-gray-600 rounded-lg hover:bg-gray-200 text-sm">
            Reset
          </button>
        </div>
      </div>

      {/* Log Table */}
      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="animate-spin text-indigo-600" size={40} /></div>
      ) : (
        <div className="bg-white shadow-xl rounded-xl overflow-hidden border border-gray-200">
          <div className="p-4 border-b bg-gray-50 flex justify-between items-center">
            <span className="font-semibold text-gray-800">
              {total} entries {filters.log_type && `(${filters.log_type})`}
            </span>
            <div className="flex gap-2">
              <button disabled={offset === 0}
                onClick={() => load(Math.max(0, offset - LIMIT))}
                className="px-3 py-1 bg-gray-200 rounded text-sm disabled:opacity-40 hover:bg-gray-300">←</button>
              <span className="px-3 py-1 text-sm text-gray-600">
                {offset + 1}–{Math.min(offset + LIMIT, total)} of {total}
              </span>
              <button disabled={offset + LIMIT >= total}
                onClick={() => load(offset + LIMIT)}
                className="px-3 py-1 bg-gray-200 rounded text-sm disabled:opacity-40 hover:bg-gray-300">→</button>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-gray-600 uppercase text-xs">
                <tr>
                  <th className="p-3 text-left">Timestamp</th>
                  <th className="p-3 text-left">Type</th>
                  <th className="p-3 text-center">Severity</th>
                  <th className="p-3 text-left">Actor</th>
                  <th className="p-3 text-left">Details</th>
                  <th className="p-3 text-center">Integrity</th>
                </tr>
              </thead>
              <tbody>
                {logs.map(log => (
                  <>
                    <tr key={log.id} className={`border-b hover:bg-gray-50 cursor-pointer
                      ${log.severity === 'CRITICAL' ? 'bg-red-50' : log.severity === 'WARN' ? 'bg-yellow-50' : ''}`}
                      onClick={() => setExpanded(expanded === log.id ? null : log.id)}>
                      <td className="p-3 font-mono text-xs text-gray-500">
                        {new Date(log.reported_at).toLocaleString()}
                      </td>
                      <td className="p-3 font-semibold text-gray-800">{log.log_type}</td>
                      <td className="p-3 text-center">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${SEV_STYLE[log.severity]}`}>
                          {SEV_ICON[log.severity]}{log.severity}
                        </span>
                      </td>
                      <td className="p-3 text-gray-600 text-xs">{log.actor_role || '—'}</td>
                      <td className="p-3 text-gray-600 text-xs max-w-xs truncate">
                        {typeof log.payload === 'object'
                          ? Object.entries(log.payload)
                              .filter(([k]) => !k.startsWith('_'))
                              .slice(0, 3)
                              .map(([k, v]) => `${k}: ${v}`)
                              .join(' | ')
                          : String(log.payload)}
                      </td>
                      <td className="p-3 text-center">
                        <span className="px-2 py-0.5 bg-gray-100 text-gray-500 rounded font-mono text-xs" title={log.hash}>
                          {log.hash?.slice(0, 8)}…
                        </span>
                      </td>
                    </tr>
                    {expanded === log.id && (
                      <tr key={`${log.id}-exp`} className="bg-gray-900 text-green-400">
                        <td colSpan={6} className="p-4">
                          <pre className="text-xs overflow-x-auto font-mono">
                            {JSON.stringify({ ...log, payload: log.payload }, null, 2)}
                          </pre>
                        </td>
                      </tr>
                    )}
                  </>
                ))}
                {logs.length === 0 && (
                  <tr><td colSpan={6} className="p-8 text-center text-gray-400">No logs found</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
